import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Divider,
  Paper,
  Typography,
  Collapse,
  Fade
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  getFieldKeys,
  getRowKeys,
  getTableColumns,
  resolveColumnField,
  upsertTableData
} from './utils/generic_utils';
import { DataTable } from 'src/components';
import { useNotif, useRequest } from 'src/hooks';
import AddDialog from './dialog';
import { useTableRows } from './useTable';
const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper
  },
  headerActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  tableContainer: {
    maxHeight: 420
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  spinnerCell: {
    padding: theme.spacing(3)
  }
}));

const useRenderGuard = (limit = 50, intervalMs = 1000) => {
  const renderCount = useRef(0);
  const firstRenderTime = useRef(Date.now());

  renderCount.current += 1;

  const now = Date.now();

  // reset counter if enough time has passed
  if (now - firstRenderTime.current > intervalMs) {
    renderCount.current = 1;
    firstRenderTime.current = now;
  }

  if (renderCount.current > limit) {
    throw new Error(
      `Potential infinite render detected: >${limit} renders in <${intervalMs}ms`
    );
  }
};

const DataGridInput = (tempData = {}) => {
  const data = tempData.data ?? {};
  const req = useRequest();
  const classes = useStyles();
  const tableSettings = data.table_Settings ?? data.table_settings ?? {};
  const source_key = data?.fld;
  const [tableData, setTbData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFolded, setIsFolded] = useState(false);
  //   columns and fields
  const columns = getTableColumns(tableSettings) ?? [];
  const rowKeys = getRowKeys(tableSettings) ?? [];
  const rowKeyField = tableSettings.row_keys ?? []; // e.g., "id2"

  async function fetchData() {
    setLoading(true);
    const payload = {
      keys: [source_key]
    };

    const response = await req.post('/setup/biz/kvs/items', payload);
    if (response.success) {
      setTbData(response.data ?? []);
    }
    setLoading(false);
  }
  // Dialog Controls
  const [isDialogOpen, setDialogState] = useState(false);
  useRenderGuard(50, 1000);

  useEffect(() => {
    fetchData();
  }, []);

  const saveCell = async (row, payload) => {
    try {
      const response = await req.post('/setup/biz/kvs/save', {
        [source_key]: payload
      });
      if (!response.success) {
        console.error(response.errorMessage || 'Failed to save cell');
        return false;
      }

      return true;
    } catch {
      console.error('Unable to save cell');
      return false;
    }
  };

  const addEmptyTableRow = (newRow = {}) => {
    setTbData(prev => {
      return [...prev, newRow];
    });
  };

  const handleCellEdit = async ({ row, field, value, col }) => {
    if (!col?.isColumnKey) return;

    const row_keys_values = rowKeys.reduce((acc, key) => {
      acc[key] = row?.[key] ?? null;
      return acc;
    }, {});

    const column_key = resolveColumnField(tableSettings.column_keys, col);
    const payloadRow = {
      ...(column_key && col?.value != null ? { [column_key]: col.value } : {}),
      value: value == null || value === '' ? 0 : Number(value),
      ...row_keys_values
    };

    const success = await saveCell(source_key, payloadRow);
    if (!success) return;
    setTbData(prev => upsertTableData(prev, payloadRow));
  };

  //  const filtered = useMemo(() => {
  //     if (!Array.isArray(tableData)) return [];

  //     if (!customFields || typeof customFields !== 'object') {
  //       return tableData;
  //     }

  //     return tableData.filter(row =>
  //       Object.entries(customFields).every(([k, v]) => {
  //         // ignore unusable filter values
  //         if (v === undefined || v === null || v === '' || v === 0) {
  //           return true;
  //         }

  //         return row?.[k] === v;
  //       })
  //     );
  //   }, [tableData, customFields]);
  const tableRows = useTableRows(tableData, tableSettings);
  return (
    <Paper className={classes.root}>
      <Box
        onClick={() => setIsFolded(prev => !prev)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          gap: '2rem'
        }}
      >
        <Box display="flex" flexDirection="column" justifyContent="center">
          <Typography variant="h3" color="textSecondary">
            {data?.sRep || 'Dashboard KVS'}
          </Typography>
          {data?.description && (
            <Typography variant="body2" color="textSecondary">
              {data.description}
            </Typography>
          )}
        </Box>
        <Fade in={!isFolded} timeout={200}>
          <Box
            display="flex"
            style={{ display: 'flex', gap: '1.5rem' }}
            justifyContent="flex-end"
          >
            <Button
              variant="text"
              color="primary"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={event => {
                event.stopPropagation(); // prevent fold toggle
                fetchData();
              }}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              startIcon={<AddIcon />}
              onClick={event => {
                event.stopPropagation(); // prevent fold toggle
                setDialogState(true);
              }}
              disabled={loading}
            >
              {`Add 'Row`}
            </Button>
          </Box>
        </Fade>
        <Fade in={isFolded} timeout={200}>
          <Box
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)'
            }}
          >
            <Typography variant="body1" color="primary">
              Click to Open
            </Typography>
          </Box>
        </Fade>
      </Box>
      <Collapse in={!isFolded} timeout={250}>
        <Box mt={2}>
          <DataTable
            columns={columns}
            data={tableRows}
            rowIDKey="pageId"
            onCellEdit={handleCellEdit}
            maxHeight={420}
            paginate={true}
            rowsPerPageOptions={[]}
            isLoading={loading}
          />
        </Box>
      </Collapse>
      <AddDialog
        open={isDialogOpen}
        onClose={() => setDialogState(false)}
        onSubmit={row => addEmptyTableRow(row)}
        fields={rowKeyField}
        source_key={source_key}
      />
    </Paper>
  );
};

DataGridInput.propTypes = {
  data: PropTypes.object
};

export default DataGridInput;
